/**
 * CIQueueDepthTile.tsx — STEP-CI-QUEUE-DEPTH-VISIBILITY
 * Displays GitHub Actions CI queue depth and runner state.
 * Auto-refreshes every 60s.
 */

import { useState, useEffect, useCallback } from "react";
import { fetchCIQueueDepth } from "../api/client.js";
import type { CIQueueDepthResponse } from "../api/types.js";

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  normal: { label: "nominal", color: "var(--ok)" },
  drain_expected: { label: "draining", color: "var(--accent)" },
  wedge_suspected: { label: "wedge suspected", color: "var(--warn)" },
  capacity_exhausted: { label: "capacity event", color: "var(--danger)" },
  runner_unavailable: { label: "runners unknown", color: "var(--warn)" },
};

export function CIQueueDepthTile() {
  const [data, setData] = useState<CIQueueDepthResponse | null>(null);
  const [error, setError] = useState<boolean>(false);

  const load = useCallback(async () => {
    try {
      const d = await fetchCIQueueDepth();
      setData(d);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  if (error && !data) {
    return (
      <div
        className="card"
        style={{ gridArea: "ciqueue", margin: 0, padding: "12px 14px" }}
        data-testid="ci-queue-depth-tile"
      >
        <TileHeader label="CI Queue" />
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Unavailable</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="card"
        style={{ gridArea: "ciqueue", margin: 0, padding: "12px 14px" }}
        data-testid="ci-queue-depth-tile"
      >
        <TileHeader label="CI Queue" />
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Loading…</div>
      </div>
    );
  }

  if (data.error) {
    return (
      <div
        className="card"
        style={{ gridArea: "ciqueue", margin: 0, padding: "12px 14px" }}
        data-testid="ci-queue-depth-tile"
      >
        <TileHeader label="CI Queue" />
        <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{data.error}</div>
      </div>
    );
  }

  const queued = data.queued_runs ?? 0;
  const inProgress = data.in_progress_runs ?? 0;
  const oldestAge = data.oldest_queued_age_min;
  const onlineRunners = data.runners
    ? data.runners.filter((r) => r.status === "online" || r.status === "busy").length
    : data.online_runners ?? 0;
  const actionKey =
    data.action === null && data.runner_api_unavailable
      ? "runner_unavailable"
      : (data.action ?? "normal");
  const actionInfo = ACTION_LABELS[actionKey] ?? ACTION_LABELS.normal;
  const isAlert = actionKey === "wedge_suspected" || actionKey === "capacity_exhausted";

  return (
    <div
      className="card"
      style={{
        gridArea: "ciqueue",
        margin: 0,
        padding: "12px 14px",
        ...(isAlert ? { borderLeft: "3px solid var(--danger)" } : {}),
      }}
      data-testid="ci-queue-depth-tile"
    >
      <TileHeader label="CI Queue" />
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: queued > 0 ? "var(--warn)" : "var(--ok)",
          }}
        >
          {queued}
        </span>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          queued
        </span>
      </div>
      <div
        style={{
          marginTop: 6,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
          {inProgress} in progress · {onlineRunners} runners online
        </div>
        {oldestAge != null && (
          <div style={{ fontSize: 10, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
            oldest queued {oldestAge} min
          </div>
        )}
        {data.runner_api_unavailable && (
          <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
            runner data unavailable
          </div>
        )}
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: actionInfo.color,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginTop: 2,
          }}
        >
          {actionInfo.label}
        </div>
      </div>
    </div>
  );
}

function TileHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: 0.7,
        color: "var(--text-dim)",
        marginBottom: 8,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  );
}
